// DineInOrdersDisplay.tsx
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  Checkbox
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Br, Cut, Image, Line, Printer as RPrinter, Row, Text, render } from 'react-thermal-printer';
import { BillItem, ITables, KITCHEN_ITEM_STATUS, ORDER_TYPE } from '../../all-types'
import { useAppDispatch } from '../../redux-store/hooks'
import { formatTimestamp, getKitchenItemStatusInfo } from '../../utils'
import { CommonBtn } from '../../components'
import { updateCartItemOfTheTable } from '../../services'
import EMPTY_BASKET from "../../assets/empty-basket.png"
import { usePrinterSettings } from '../../hooks';

// Define the statuses relevant for individual kitchen items
const kitchenStatuses = Object.values(KITCHEN_ITEM_STATUS).filter(
  (status) =>
    status !== KITCHEN_ITEM_STATUS.ALL && status !== KITCHEN_ITEM_STATUS.IN_CART
)

interface DineInOrdersDisplayProps {
  allTables: ITables[],
  checkedItems: { [itemId: string]: boolean },
  setCheckedItems: React.Dispatch<React.SetStateAction<{
    [itemId: string]: boolean;
  }>>;
}

export const DineInOrdersDisplay: React.FC<DineInOrdersDisplayProps> = ({
  allTables, checkedItems, setCheckedItems
}) => {
  const { printerUsb, printerStatus } = usePrinterSettings();
  const dispatch = useAppDispatch()
  const [selectedTab, setSelectedTab] = useState<KITCHEN_ITEM_STATUS>(
    KITCHEN_ITEM_STATUS.IN_KITCHEN
  )

  useEffect(() => {
    setCheckedItems({});
  }, [selectedTab])

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: KITCHEN_ITEM_STATUS
  ) => {
    setSelectedTab(newValue)
  }

  const handleStatusChange = async (
    sno: number,
    tableNo: number,
    newStatus: KITCHEN_ITEM_STATUS
  ) => {
    try {
      const table = allTables.find((t) => t.tableNo === tableNo)
      const clonedTable: ITables = JSON.parse(JSON.stringify(table))

      const billSession = table?.billSession
      if (billSession) {
        const items = [...(billSession.items ?? [])]
        const editIndex = items.findIndex((i) => i.sno === sno)
        if (editIndex > -1) {
          items[editIndex] = {
            ...items[editIndex],
            kitchInfo: {
              ...items[editIndex].kitchInfo,
              status: newStatus,
            },
          }
          clonedTable.billSession = {
            ...billSession,
            items,
          }
          await dispatch(updateCartItemOfTheTable({ table: clonedTable }))
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  // Filter only dine-in items from all tables with active bill sessions
  const allDineInItems = allTables
    .filter(
      (table) =>
        table.billSession &&
        table.billSession.items &&
        table.billSession.items.length > 0
    )
    .flatMap((table) =>
      table.billSession!.items!.map((item) => ({
        ...item,
        tableNo: table.tableNo,
        billNo: table.billSession?.billNo,
        orderType: ORDER_TYPE.DINE_IN, // Explicitly mark as dine-in
      }))
    )

  const filteredItems = allDineInItems.filter(
    (item) => item.kitchInfo?.status === selectedTab
  )

  const checkedItemsNumber = Object.keys(checkedItems).reduce((total, key) => {
    return total + (checkedItems[key] ? 1 : 0);
  }, 0);


  const groupedByTable = filteredItems.reduce<
    Record<string, typeof filteredItems>
  >((acc, item) => {
    const key = item.tableNo + '' // Use tableNo as key for grouping
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  // Sort tables by earliest order time
  const sortedTables = Object.entries(groupedByTable).sort(
    ([, itemsA], [, itemsB]) => {
      const getEarliestTime = (items: typeof filteredItems) => {
        return Math.min(
          ...items.map((item) =>
            item?.kitchInfo?.itemReceviedTime
              ? Number(item.kitchInfo.itemReceviedTime)
              : Infinity
          )
        )
      }

      const timeA = getEarliestTime(itemsA)
      const timeB = getEarliestTime(itemsB)

      return timeA - timeB
    }
  )

  const getNextStatusOptions = (currentStatus: KITCHEN_ITEM_STATUS) => {
    switch (currentStatus) {
      case KITCHEN_ITEM_STATUS.IN_KITCHEN:
        return [KITCHEN_ITEM_STATUS.COOKING, KITCHEN_ITEM_STATUS.CANCELLED]
      case KITCHEN_ITEM_STATUS.COOKING:
        return [KITCHEN_ITEM_STATUS.READY, KITCHEN_ITEM_STATUS.CANCELLED]
      case KITCHEN_ITEM_STATUS.READY:
        return [KITCHEN_ITEM_STATUS.SERVED]
      default:
        return []
    }
  }

  const getKitchenStatusLabel = (status: KITCHEN_ITEM_STATUS) => {
    switch (status) {
      case KITCHEN_ITEM_STATUS.IN_KITCHEN:
        return 'In Kitchen'
      case KITCHEN_ITEM_STATUS.COOKING:
        return 'Cooking'
      case KITCHEN_ITEM_STATUS.READY:
        return 'Ready'
      case KITCHEN_ITEM_STATUS.SERVED:
        return 'Served'
      case KITCHEN_ITEM_STATUS.CANCELLED:
        return 'Cancelled'
      default:
        return '' // Should not be reached
    }
  }

  const markStatusColors = (status: KITCHEN_ITEM_STATUS) => {
    switch (status) {
      case KITCHEN_ITEM_STATUS.COOKING:
        return 'color4.main'
      case KITCHEN_ITEM_STATUS.READY:
        return 'occupied.main'
      case KITCHEN_ITEM_STATUS.SERVED:
        return 'color3.main'
      case KITCHEN_ITEM_STATUS.CANCELLED:
        return 'occupiedTable.main'
      default:
        return ''
    }
  }
  const markStatusBackground = (status: KITCHEN_ITEM_STATUS) => {
    switch (status) {
      case KITCHEN_ITEM_STATUS.COOKING:
        return '#FFFAED'
      case KITCHEN_ITEM_STATUS.READY:
        return '#FFF9ED'
      case KITCHEN_ITEM_STATUS.SERVED:
        return 'color8.main'
      case KITCHEN_ITEM_STATUS.CANCELLED:
        return '#FFFAED'
      default:
        return ''
    }
  }

  const getInstructions = (inst: string) => {
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst)
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      )
    } catch (e) {
      console.error(e)
    }
  }

  const printSelectedItems = async (items: BillItem[], tableNo: number) => {
    try {

      const data = await render(
        <RPrinter type='epson' characterSet='pc437_usa'>
          <Text font={"A"} size={{ width: 2, height: 2 }} align="center" bold={true}>
            {"KITCHEN TOKEN"}
          </Text>
          <Line />
          <Text font={"A"} size={{ width: 2, height: 2 }} align="center" bold={true}>
            {"TABLE NO #"}{tableNo}
          </Text>
          <Line />
          <Br />
          {items?.map(i => {
            const isPresent = checkedItems[i.sno];
            return (
              <>
                {isPresent && (
                  <>
                    <Text font={"A"} size={{ width: 2, height: 2 }} bold={true} >
                      {i.itemName}
                    </Text>
                    <Text font={"A"} size={{ width: 2, height: 2 }} bold={true} >
                      x{i.noOfItems} ({i.quantity}{i.quantityType})
                    </Text>
                  </>
                )}
              </>
            )
          })}
          <Cut />
        </RPrinter>
      );

      if (printerUsb) {
        await printerUsb.print(data);
      }


    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Box>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {kitchenStatuses.map((status) => (
          <Tab
            key={status}
            label={getKitchenStatusLabel(status)}
            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
            value={status}
          />
        ))}
      </Tabs>

      {sortedTables.length === 0 && (
        <Stack justifyContent={"center"} alignItems={"center"}>
          <img src={EMPTY_BASKET} style={{ width: "250px", height: "250px" }}></img>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mt: 4, textAlign: 'center', fontWeight: "bold" }}
          >
            No dine-in orders in '{getKitchenStatusLabel(selectedTab)}' status.
          </Typography>
        </Stack>
      )}

      {sortedTables.map(([tableKey, items]) => (
        <Box key={tableKey} mb={4}>

          <Stack flexDirection={"row"} alignItems={"center"} gap={2}>
            <Typography variant="h6" gutterBottom>
              Table: #{items[0].tableNo}
            </Typography>
            {(checkedItemsNumber > 0 && (selectedTab === KITCHEN_ITEM_STATUS.SERVED ||
              selectedTab === KITCHEN_ITEM_STATUS.READY))
              && <CommonBtn onClick={() => printSelectedItems(items, items[0].tableNo)}
                sx={{ bgcolor: "CommonBlack.main", color: "CommonWhite.main" }}
                variant='contained' >{checkedItemsNumber} Print</CommonBtn>}
          </Stack>

          <Grid container spacing={2} mt={2}>
            {items.map((item, index) => {
              const currentStatus = item.kitchInfo?.status
              const nextStatusOptions = getNextStatusOptions(currentStatus)

              return (
                <Grid item xs={12} md={4} lg={4} key={index}>
                  <Card elevation={4} sx={{
                    height: '100%', borderRadius: 4,
                    backgroundColor: "primary.light"
                  }}>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="h6" fontWeight={'bold'}
                          sx={{ alignItems: "center", display: "flex" }}>
                          {(item.kitchInfo?.status === KITCHEN_ITEM_STATUS.READY
                            || item.kitchInfo?.status === KITCHEN_ITEM_STATUS.SERVED)
                            && <Checkbox checked={checkedItems[item.sno] || false}
                              onChange={(e) => {
                                setCheckedItems(prevState => ({
                                  ...prevState,
                                  [item.sno]: e.target.checked
                                }));
                              }}
                            />}

                          {item?.itemName}
                        </Typography>
                        <Chip
                          label={getKitchenItemStatusInfo(currentStatus).displayText}
                          color={getKitchenItemStatusInfo(currentStatus).color}
                          size="small"
                        />
                      </Box>
                      <Box display={'flex'} flexDirection={'column'} gap={1}>

                        <Stack flexDirection={"row"} justifyContent={"space-between"}>
                          <Chip
                            label={"Full"}
                            color={getKitchenItemStatusInfo("dine-in").color}
                            size="small"

                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            Quantity:
                            <Box
                              component={'span'}
                              sx={{ ml: 2, fontWeight: '400' }}
                            >
                              x{item.noOfItems}
                            </Box>
                          </Typography>
                        </Stack>


                        <Typography
                          variant="body2"
                          color="text.secondary"
                          display={'flex'}
                          gap={1}
                        >
                          <Box component={'span'} fontWeight={600}>
                            Instructions:
                          </Box>
                          <Box component={'span'} fontWeight={400}>
                            {getInstructions(
                              item?.kitchInfo?.kitchenInstrcutions
                            ) || 'None'}
                          </Box>
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          Order Time:
                          <Box
                            component={'span'}
                            sx={{ ml: 1 }}
                            fontWeight={400}
                          >
                            {item?.kitchInfo?.itemReceviedTime
                              ? formatTimestamp(
                                Number(item?.kitchInfo?.itemReceviedTime)
                              )
                              : '-'}
                          </Box>
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 2 }} />

                      <Stack>



                        <Box display="flex" gap={1} flexWrap="wrap">

                          {nextStatusOptions?.map((status) => (
                            <CommonBtn
                              key={status}
                              variant="contained"
                              size="small"
                              sx={{
                                bgcolor: `${getKitchenItemStatusInfo(status).bgColor}`,
                                color: `${getKitchenItemStatusInfo(status).textColor}`,
                              }}
                              onClick={() =>
                                handleStatusChange(
                                  item.sno,
                                  item.tableNo,
                                  status
                                )
                              }
                            >
                              Mark as {getKitchenStatusLabel(status)}
                            </CommonBtn>
                          ))}
                          {/* {currentStatus === KITCHEN_ITEM_STATUS.SERVED || currentStatus === KITCHEN_ITEM_STATUS.READY && (
                          <Box display={'flex'} justifyContent={"center"}>
                            <CommonBtn sx={{ bgcolor: "CommonBlack.main", color: "CommonWhite.main" }}>Print</CommonBtn>
                          </Box>)} */}
                        </Box>


                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  )
}
